import {ReactElement, useContext, useEffect, useMemo, useState} from "react";
import {
    useDeleteUsersMutation,
    useListGroupsQuery,
    useListUserClientsPropertiesQuery,
    useListUsersLazyQuery,
    UserRole
} from "../../../__generated__/generated_types";
import {User} from "../types";
import {Body, Cell, Head, HeaderCell, HeaderRow, Row, Table} from "@zendeskgarden/react-tables";
import {Checkbox, Field, Label, MediaInput} from '@zendeskgarden/react-forms';
import {ViziblyTheme} from "../../analyst/ViziblyZDGTheme";
import {ThemeProvider} from "@zendeskgarden/react-theming";
import {formatterEnumToTitle} from "../../../utils/formatters";
import {CheckRounded, SearchRounded} from "@material-ui/icons";
import {Button} from "@zendeskgarden/react-buttons";
import {UserCreateModal} from "./UserCreateModal";
import {UserEditModal} from "./UserEditModal";

import * as css from "./styles/userStyles.module.scss";
import AdminHeader from "../AdminHeader";
import SelectionBar from "./components/SelectionBar";
import UserDeleteConfirmation from "./components/UserDeleteConfirmation";
import {Inline} from "@zendeskgarden/react-loaders";
import {COLORS} from "../../../constants/Colors";
import {toast} from "react-toastify";
import {authorizationTitleRemapping} from "./logic/authorizationRenamer";
import {UserBulkEditModal} from "./UserBulkEditModal";
import {AuthContext} from "../../../contexts/AuthContext";
import {CustomZDDropdown} from "../../../atoms/custom-zd-dropdown/CustomZDDropdown";
import {Authorizations} from "../../../authorization/Authorizations";
import {userIsAdmin} from "../../../authorization/AuthorizationCheckers";
import {Tooltip} from "@zendeskgarden/react-tooltips";

import "native-injects";

const NO_GROUP = {value: "no_group", label: "NO GROUP"};

export function UserList(props: {authData: string[]}): ReactElement {
    const auth = useContext(AuthContext);
    const [getUsers, { loading, data }] = useListUsersLazyQuery({
        fetchPolicy: "no-cache"
    });
    const {loading: propertiesLoading, data: propertiesData} = useListUserClientsPropertiesQuery({
        fetchPolicy: "no-cache"
    });
    const {data: groupsData, loading: groupsLoading} = useListGroupsQuery({
        fetchPolicy: "no-cache"
    });
    const [users, setUsers] = useState<User[]>([]);
    const [search, setSearch] = useState<string | undefined>(undefined);
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [selectedProperties, setSelectedProperties] = useState<string[]>();
    const allPropertiesSelected = useMemo(() => {
        const propertyIds = new Set(propertiesData?.listUserClientsProperties.map(p => p.id) ?? []);
        const selectedPropertyIds = new Set(selectedProperties ?? []);
        return propertyIds.size === selectedPropertyIds.size;
    }, [propertiesData, selectedProperties]);
    const [selectedUserGroups, setSelectedUserGroups] = useState<string[]>();
    const [selectedRoles, setSelectedRoles] = useState<UserRole[]>([UserRole.Admin, UserRole.Manager]);
    const [deleteUsers, { loading: loadingDeleteUsers }] = useDeleteUsersMutation({
        notifyOnNetworkStatusChange: true,
    });
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [createFormVisible, setCreateFormVisible] = useState<boolean>(false);
    const [editFormVisible, setEditFormVisible] = useState<boolean>(false);
    const [bulkEditVisible, setBulkEditVisible] = useState<boolean>(false);
    const [confirmDeleteVisible, setConfirmDeleteVisible] = useState<boolean>(false);

    function canSelect(user: User): boolean {
        return userIsAdmin(auth.user) || user.role !== UserRole.Admin;
    }

    function renderRowCheckbox(user: User): ReactElement {
        const checkbox = (
            <Field>
                <Checkbox
                    className={css.userTableCheckbox}
                    checked={selectedUsers.includes(user.id)}
                    disabled={!canSelect(user)}
                    onChange={() => {
                        selectedUsers.includes(user.id) ? deselectUser(user.id) : selectUser(user.id);
                    }}
                >
                    <Label className={css.userTableCheckbox} />
                </Checkbox>
            </Field>
        );

        if(canSelect(user)) {
            return checkbox;
        }
        return (
            <Tooltip type="light" content="Admin users can only be managed by Administrators" appendToNode={document.body} zIndex={110}>
                <span>{checkbox}</span>
            </Tooltip>
        );
    }

    const selectableUsers = filteredUsers.filter(canSelect);
    const allSelected = selectableUsers.length > 0 && selectedUsers.length == selectableUsers.length;

    function selectAll(): void {
        setSelectedUsers(selectableUsers.map(x => x.id));
    }

    function deselectAll(): void {
        setSelectedUsers([]);
    }

    function selectUser(user: string): void {
        setSelectedUsers([...selectedUsers, user]);
    }

    function deselectUser(user: string): void {
        const newSelection = [...selectedUsers];
        newSelection.splice(newSelection.indexOf(user), 1);
        setSelectedUsers(newSelection);
    }

    function deleteSelectedUsers(): void {
        deleteUsers({
            variables: {
                ids: selectedUsers,
            }
        })
            .then(() => {
                setConfirmDeleteVisible(false);
                deselectAll();
                toast.success('Deleted successfully');
                getUsers();
            })
            .catch(() => { /* Do Nothing */ });
    }

    useEffect(() => {
        getUsers();

        const handleEsc = (evt: KeyboardEvent) => {
            if (evt.key === 'Escape') {
                deselectAll();
            }
        };

        window.addEventListener('keydown', handleEsc);

        return () => {
            window.removeEventListener('keydown', handleEsc);
        };
    }, [auth.user?.clientId]);

    useEffect(() => {
        if(groupsLoading || !groupsData) {
            return;
        }
        if(groupsData.listUserGroups.length > 0) {
            setSelectedUserGroups(groupsData.listUserGroups.map(ug => ug.id).concat([NO_GROUP.value]));
        }
    }, [groupsLoading, groupsData]);

    useEffect(() => {
        if(propertiesLoading || !propertiesData) {
            return;
        }
        if(propertiesData.listUserClientsProperties.length > 0) {
            setSelectedProperties(propertiesData.listUserClientsProperties.map(p => p.id));
        }
    }, [propertiesLoading, propertiesData]);

    useEffect(() => {
        if (!loading && data?.listUsers) {
            setUsers(data.listUsers);
        }
    }, [loading, data]);

    useEffect(() => {
        if (users.length === 0) {
            return;
        }

        const filtered: User[] = [];
        for (const user of users) {
            let include = !search
                || user.firstName.toLowerCase().includes(search)
                || user.lastName.toLowerCase().includes(search)
                || user.email.toLowerCase().includes(search);
            if(include) {
                include = selectedRoles.includes(user.role);
            }
            if(include) {
                include = (
                    !selectedUserGroups
                    || user.groups.length == 0 && selectedUserGroups.includes(NO_GROUP.value)
                    || selectedUserGroups.some(groupId => user.groups.find(ug => ug.id == groupId))
                );
            }
            if(include) {
                include = (
                    allPropertiesSelected
                    || !selectedProperties
                    || user.role == UserRole.Admin
                    || user.authorizations.includes(Authorizations.AccessAllProperties)
                    || selectedProperties.some(propertyId => user.properties.find(up => up.id == propertyId))
                );
            }
            if(include && !userIsAdmin(auth.user)) {
                include = user.id !== auth.user?.id;
            }
            if(include) {
                filtered.push(user);
            }
        }

        setFilteredUsers(filtered);
    }, [search, users, allPropertiesSelected, selectedProperties, selectedUserGroups, selectedRoles]);

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <div className={css.userPageInner}>
                <AdminHeader
                    title={"User Management"}
                    subtitle={"Add new users, delete users, and configure permissions."}
                />

                <div className={css.tableHeaderActions}>
                    <h5 className={css.tableHeaderActionsLeft}>
                        Users
                        {
                            loading && <Inline size={24} color={COLORS.PRIMARY_500} aria-label="loading"/>
                        }
                    </h5>
                    <div className={css.tableHeaderActionsRight}>
                        <Field>
                            <MediaInput
                                className={css.tableHeaderActionsSearch}
                                placeholder="Search"
                                isCompact
                                onChange={evt => setSearch(evt.target.value.toLowerCase())}
                                start={
                                    <SearchRounded />
                                }
                            />
                        </Field>
                        <Button isPrimary className={css.tableHeaderActionsButton} onClick={() => setCreateFormVisible(true)}>Add User</Button>
                    </div>
                </div>

                <div className={css.userTableWrapper}>
                    <Table className={css.userTable} style={{width: "auto"}} size={"large"} >
                        <Head isSticky>
                            <HeaderRow className={css.headerRowAbove}>
                                <HeaderCell className={css.checkboxCell} />
                                <HeaderCell className={`${css.nameCell} ${css.firstNameCell}`} />
                                <HeaderCell className={`${css.nameCell} ${css.lastNameCell}`} />
                                <HeaderCell />
                                <HeaderCell />
                                <HeaderCell />
                                <HeaderCell />
                                <HeaderCell colSpan={props.authData.length + 1} className={css.permissionsHeader}>PERMISSIONS</HeaderCell>
                            </HeaderRow>

                            <HeaderRow className={css.headerRow}>
                                <HeaderCell isMinimum className={css.checkboxCell}>
                                    <Field>
                                        <Checkbox
                                            className={css.userTableCheckbox}
                                            checked={!loading && allSelected}
                                            disabled={selectableUsers.length === 0}
                                            onChange={() => {
                                                allSelected ? deselectAll() : selectAll();
                                            }}
                                            indeterminate={selectedUsers.length != 0 && selectedUsers.length != selectableUsers.length}
                                        >
                                            <Label className={css.userTableCheckbox} />
                                        </Checkbox>
                                    </Field>
                                </HeaderCell>
                                <HeaderCell className={`${css.nameCell} ${css.firstNameCell}`}>First Name</HeaderCell>
                                <HeaderCell className={`${css.nameCell} ${css.lastNameCell}`}>Last Name</HeaderCell>
                                <HeaderCell className={css.emailCell}>Email</HeaderCell>
                                <HeaderCell>
                                    Role
                                    <CustomZDDropdown
                                        initialSelectedItems={selectedRoles}
                                        applySelectedItems={function (items: any): void {
                                            setSelectedRoles(items);
                                        }}
                                        openDropdownPlaceholder={"Select Role(s)"}
                                        closedDropdownPlaceholder={undefined}
                                        options={[
                                            {
                                                value: UserRole.Admin,
                                                label: "Admin"
                                            },
                                            {
                                                value: UserRole.Manager,
                                                label: "Manager"
                                            }
                                        ]}
                                        isError={undefined}
                                        isMulti
                                        allOption
                                    />
                                </HeaderCell>
                                <HeaderCell className={css.groupsCell}>
                                    Group(s)
                                    <CustomZDDropdown
                                        initialSelectedItems={selectedUserGroups}
                                        applySelectedItems={function (items: any): void {
                                            setSelectedUserGroups(items);
                                        }}
                                        openDropdownPlaceholder={"Select Group(s)"}
                                        closedDropdownPlaceholder={undefined}
                                        options={groupsData ?
                                            [NO_GROUP].concat(
                                                groupsData.listUserGroups
                                                    .map(ug => ({value: ug.id, label: ug.name}))
                                                    .sortBy("label")
                                            )
                                            :
                                            [NO_GROUP]
                                        }
                                        isError={undefined}
                                        isMulti
                                        allOption
                                    />
                                </HeaderCell>
                                <HeaderCell className={css.propertiesCell}>
                                    Properties
                                    <CustomZDDropdown
                                        initialSelectedItems={selectedProperties}
                                        applySelectedItems={function (items: any): void {
                                            setSelectedProperties(items);
                                        }}
                                        openDropdownPlaceholder={"Select Property(ies)"}
                                        closedDropdownPlaceholder={undefined}
                                        options={propertiesData ? propertiesData.listUserClientsProperties.map(p => ({value: p.id, label: p.name})).sortBy("label") : []}
                                        isError={undefined}
                                        isMulti
                                        allOption
                                    />
                                </HeaderCell>
                                <HeaderCell className={css.headerAuthCell}>Read Only</HeaderCell>
                                {props.authData.map((auth, i) => (
                                    <HeaderCell key={i} className={css.headerAuthCell}>{formatterEnumToTitle(authorizationTitleRemapping(auth))}</HeaderCell>
                                ))}
                            </HeaderRow>
                        </Head>

                        <Body>
                            {filteredUsers.map(u => (
                                <Row key={u.id} className={canSelect(u) ? css.bodyRow : `${css.bodyRow} ${css.disabledRow}`}>
                                    <Cell className={css.checkboxCell}>
                                        {renderRowCheckbox(u)}
                                    </Cell>
                                    <Cell className={`${css.nameCell} ${css.firstNameCell}`}>{u.firstName}</Cell>
                                    <Cell className={`${css.nameCell} ${css.lastNameCell}`}>{u.lastName}</Cell>
                                    <Cell>{u.email}</Cell>
                                    <Cell>{formatterEnumToTitle(u.role)}</Cell>
                                    <Cell className={css.groupsCell}>
                                        {u.groups.map(g => g.name).join(",")}
                                    </Cell>
                                    <Cell className={css.propertiesCell}>
                                        {u.role == UserRole.Admin ? "All" : u.properties.map(x => x.name).join(", ")}
                                    </Cell>
                                    <Cell className={css.authCell}>{u.isReadOnly ? <CheckRounded /> : <></>}</Cell>
                                    {props.authData.map((auth, i) => (
                                        <Cell key={i} className={css.authCell}>
                                            {
                                                u.authorizations.includes(auth) || u.role == UserRole.Admin
                                                    ? <CheckRounded />
                                                    : <></>
                                            }
                                        </Cell>
                                    ))}
                                </Row>
                            ))}
                        </Body>
                    </Table>
                </div>
            </div>

            <SelectionBar
                selected={selectedUsers}
                onEdit={() => {
                    if(selectedUsers.length > 1) {
                        setBulkEditVisible(true);
                    } else {
                        setEditFormVisible(true);
                    }
                }}
                onDelete={() => setConfirmDeleteVisible(true)}
                name="User"
                namePlural="Users"
                enableBulk
            />
            <UserCreateModal
                formVisible={createFormVisible}
                onModalClose={() => setCreateFormVisible(false)}
                onCreate={getUsers}
            />
            <UserEditModal
                userId={selectedUsers[0]}
                formVisible={editFormVisible}
                onModalClose={() => setEditFormVisible(false)}
                onUpdate={getUsers}
            />
            {bulkEditVisible &&
                <UserBulkEditModal
                        closed={edited => {
                            setBulkEditVisible(false);
                            if(edited) {
                                getUsers();
                            }
                        }}
                        userIds={selectedUsers}
                />
            }

            {confirmDeleteVisible && (
                <UserDeleteConfirmation
                    users={users}
                    selectedUsers={selectedUsers}
                    loading={loadingDeleteUsers}
                    onClose={() => setConfirmDeleteVisible(false)}
                    onConfirm={deleteSelectedUsers}
                />
            )}
        </ThemeProvider>
    );
}
